/**
 * "Tabs" dropdown: turn a view into a tab, and edit or remove the tabs already saved.
 *
 * Deliberately mirrors TableSavedViewsControl's interaction language (row list,
 * click-the-name to rename inline with Enter/Escape, trailing trash icon, a shared
 * `busy` guard) since that is the established convention for this kind of control.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Layers,
  ChevronDown,
  GripHorizontal,
  Loader2,
  Plus,
  Save,
  Trash2,
  Check,
  X,
} from "lucide-react";
import ConfirmDeleteModal from "../../../../components/ACS-Dashboard/ACS-Blocks/ConfirmDeleteModal";

const ShipmentViewTabsControl = ({
  viewTabs = [],
  loading = false,
  error = null,
  /** Set when this table *is* a view tab, which enables "Update this tab". */
  currentViewTabId = null,
  /** Saved views of the table this control sits in, offered as tab sources. */
  views = [],
  activeView = null,
  captureConfig,
  onCreate,
  onRename,
  onDelete,
  onUpdateConfig,
  onMove,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newTabName, setNewTabName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // The tab awaiting a confirmed removal; also what the modal renders.
  const [pendingDeleteTab, setPendingDeleteTab] = useState(null);
  const containerRef = useRef(null);
  const dragSourceId = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setRenamingId(null);
    setRenameValue("");
    setShowCreateInput(false);
    setNewTabName("");
    // A drag interrupted by the panel closing would otherwise leave a stale source
    // that the next drop would act on.
    dragSourceId.current = null;
    setDragOverId(null);
    setPendingDeleteTab(null);
  }, [isOpen]);

  const run = async (fn) => {
    setBusy(true);
    setLocalError(null);
    try {
      await fn();
    } catch (err) {
      setLocalError(
        err?.response?.data?.message || err?.message || "Something went wrong"
      );
    } finally {
      setBusy(false);
    }
  };

  const openCreateInput = () => {
    // Pre-fill with the view being pinned so the common case is one click + Enter.
    setNewTabName(activeView?.name || "");
    setShowCreateInput(true);
  };

  const handleCreateFromCurrent = () =>
    run(async () => {
      const name = newTabName.trim();
      if (!name) return;
      // Flagged so the host can tell this apart from pinning some other saved view:
      // this one pins the tab the user is looking at.
      await onCreate?.(name, captureConfig?.() || {}, { fromCurrentView: true });
      setNewTabName("");
      setShowCreateInput(false);
      setIsOpen(false);
    });

  const handleCreateFromView = (view) =>
    run(async () => {
      await onCreate?.(view.name, view.config || {});
      setIsOpen(false);
    });

  const handleUpdateCurrent = () =>
    run(async () => {
      await onUpdateConfig?.(currentViewTabId, captureConfig?.() || {});
      setIsOpen(false);
    });

  const startRename = (tab, e) => {
    e?.stopPropagation?.();
    setRenamingId(tab.id);
    setRenameValue(tab.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = (tabId) =>
    run(async () => {
      const trimmed = renameValue.trim();
      if (!trimmed) return;
      await onRename?.(tabId, trimmed);
      cancelRename();
    });

  // Drag to reorder, mirroring the column manager: a source held in a ref, the
  // hovered row highlighted, and the drop resolved by id rather than index.
  const handleDrop = (targetTab) => {
    const sourceId = dragSourceId.current;
    dragSourceId.current = null;
    setDragOverId(null);
    if (sourceId == null || String(sourceId) === String(targetTab.id)) return;
    run(async () => {
      await onMove?.(sourceId, targetTab.id);
    });
  };

  const handleDelete = (tab, e) => {
    e?.stopPropagation?.();
    setPendingDeleteTab(tab);
  };

  const confirmDelete = () =>
    run(async () => {
      await onDelete?.(pendingDeleteTab.id);
      setPendingDeleteTab(null);
    });

  const canMove = typeof onMove === "function";

  // Views already pinned under the same name are dropped from the "add from view"
  // list so it reads as the set of things still worth adding.
  const pinnedNames = new Set(viewTabs.map((t) => t.name));
  const pinnableViews = views.filter((v) => !pinnedNames.has(v.name));
  const displayError = localError || error;

  return (
    <div ref={containerRef} className={`relative w-fit ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-all duration-150 hover:border-[#223688] hover:text-[#223688] disabled:opacity-60 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text dark:hover:border-[#223688] dark:hover:text-[#223688]"
      >
        {loading ? (
          <Loader2 size={15} className="shrink-0 animate-spin" />
        ) : (
          <Layers size={15} className="shrink-0" />
        )}
        <span>Tabs{viewTabs.length ? ` (${viewTabs.length})` : ""}</span>
        <ChevronDown
          size={15}
          className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-[200] mt-2 min-w-[300px] rounded-md border border-gray-200 bg-white shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
          {displayError && (
            <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              {displayError}
            </p>
          )}

          <div className="max-h-[240px] overflow-y-auto py-1">
            {viewTabs.length === 0 && !loading && (
              <p className="px-4 py-2 text-xs text-gray-500 dark:text-acs-dark-subtext">
                No saved tabs yet. Tabs you add here stay after you log out.
              </p>
            )}

            {viewTabs.map((tab) => {
              const isRenaming = renamingId === tab.id;
              const isDragOver = canMove && String(dragOverId) === String(tab.id);

              return (
              <div
                key={tab.id}
                // Not draggable mid-rename: the row would steal the pointer from
                // the text input.
                draggable={canMove && !isRenaming && !busy}
                onDragStart={() => {
                  dragSourceId.current = tab.id;
                }}
                onDragOver={(e) => {
                  if (dragSourceId.current == null) return;
                  e.preventDefault();
                  setDragOverId(tab.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(tab);
                }}
                onDragEnd={() => {
                  dragSourceId.current = null;
                  setDragOverId(null);
                }}
                className={`group px-2 py-1 ${
                  canMove && !isRenaming ? "cursor-grab select-none" : ""
                } ${
                  isDragOver
                    ? "rounded-md border border-[#223688]/40 bg-[#223688]/10 dark:border-[#5d6fc0]/50 dark:bg-[#223688]/25"
                    : "border border-transparent hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                } ${
                  !isDragOver && String(tab.id) === String(currentViewTabId)
                    ? "bg-[#223688]/10 dark:bg-[#223688]/30"
                    : ""
                }`}
              >
                {renamingId === tab.id ? (
                  <div
                    className="flex items-center gap-1 px-1 py-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      placeholder="Tab name"
                      autoFocus
                      className="min-w-0 flex-1 rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitRename(tab.id);
                        if (e.key === "Escape") cancelRename();
                      }}
                    />
                    <button
                      type="button"
                      title="Save name"
                      disabled={busy || !renameValue.trim()}
                      className="rounded p-1 text-[#223688] hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
                      onClick={() => commitRename(tab.id)}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      type="button"
                      title="Cancel edit"
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                      onClick={cancelRename}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 px-1 py-1.5">
                    {canMove && (
                      <GripHorizontal
                        size={13}
                        className="mr-1.5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-400 dark:text-gray-600"
                      />
                    )}
                    <button
                      type="button"
                      title="Click to rename"
                      disabled={busy}
                      className={`min-w-0 flex-1 truncate text-left text-sm hover:underline ${
                        String(tab.id) === String(currentViewTabId)
                          ? "font-semibold"
                          : ""
                      }`}
                      onClick={(e) => startRename(tab, e)}
                    >
                      {tab.name}
                    </button>
                    <button
                      type="button"
                      title="Remove tab"
                      disabled={busy}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                      onClick={(e) => handleDelete(tab, e)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          <div className="border-t border-gray-200 p-2 dark:border-acs-dark-border">
            {showCreateInput ? (
              <div className="flex flex-col gap-2">
                <input
                  value={newTabName}
                  onChange={(e) => setNewTabName(e.target.value)}
                  placeholder="Tab name"
                  autoFocus
                  className="w-full rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateFromCurrent();
                    if (e.key === "Escape") setShowCreateInput(false);
                  }}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 rounded bg-[#223688] px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    disabled={busy || !newTabName.trim()}
                    onClick={handleCreateFromCurrent}
                  >
                    Add tab
                  </button>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-gray-600 dark:text-acs-dark-subtext"
                    onClick={() => setShowCreateInput(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  disabled={busy}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                  onClick={openCreateInput}
                >
                  <Plus size={14} /> Add current view as tab
                </button>

                {currentViewTabId != null && (
                  <button
                    type="button"
                    disabled={busy}
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                    onClick={handleUpdateCurrent}
                  >
                    <Save size={14} /> Save this layout to the tab
                  </button>
                )}

                {pinnableViews.length > 0 && (
                  <div className="mt-1 border-t border-gray-200 pt-1 dark:border-acs-dark-border">
                    <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-acs-dark-subtext">
                      Add tab from saved view
                    </p>
                    {pinnableViews.map((view) => (
                      <button
                        key={view.id}
                        type="button"
                        disabled={busy}
                        className="flex w-full items-center gap-2 truncate rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                        onClick={() => handleCreateFromView(view)}
                      >
                        <Plus size={14} className="shrink-0" />
                        <span className="truncate">{view.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={pendingDeleteTab != null}
        title="Remove tab"
        headline={`Remove the \u201c${pendingDeleteTab?.name ?? ""}\u201d tab?`}
        description="This cannot be undone. The saved views inside the tab are not affected — only the tab itself is removed."
        confirmLabel="Remove tab"
        busy={busy}
        error={localError}
        onCancel={() => setPendingDeleteTab(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default ShipmentViewTabsControl;
